import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import api from '../../api/api';
import ProcurementPlanningTabs from '../../components/ProcurementPlanningTabs';
import ProcurementAiAssistant from '../../components/ProcurementAiAssistant';
import './OrderSupplyPage.css';

export interface SupplyProduct {
  productId: number;
  productName: string;
  unit: string;
  required: number;
  covered: number;
  missing: number;
}
export interface SupplyOrder {
  orderId: number;
  eventDate: string;
  eventName: string;
  customerName: string;
  products: SupplyProduct[];
}
export interface SupplySummary extends SupplyProduct {
  neededBy: string | null;
  orders: Array<{ orderId: number; eventDate: string; required: number; covered: number; missing: number }>;
}
export interface SupplyPlan {
  generatedAtUtc: string;
  from: string;
  to: string;
  orders: SupplyOrder[];
  products: SupplySummary[];
}

const quantity = (value: number) => value.toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const date = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
};
const Amounts: React.FC<{ item: Pick<SupplyProduct, 'required' | 'covered' | 'missing' | 'unit'> }> = ({ item }) => (
  <dl className="order-supply-amounts">
    <div><dt>Потрібно</dt><dd>{quantity(item.required)} {item.unit}</dd></div>
    <div><dt>Покрито запасом</dt><dd>{quantity(item.covered)} {item.unit}</dd></div>
    <div className={item.missing > 0 ? 'order-supply-deficit' : ''}>
      <dt>Бракує</dt><dd>{quantity(item.missing)} {item.unit}</dd>
    </div>
  </dl>
);

const OrderSupplyPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const selectedOrder = params.get('eventOrderId');
  const [days, setDays] = useState(30);
  const [mode, setMode] = useState<'orders' | 'summary'>('orders');
  const [showCovered, setShowCovered] = useState(false);
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState<SupplyPlan | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const selectedRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setPlan(null);
    api.get<SupplyPlan>('/notifications/order-supply', {
      params: { days, ...(selectedOrder ? { eventOrderId: selectedOrder } : {}) },
      signal: controller.signal,
    }).then(({ data }) => {
      if (!controller.signal.aborted) setPlan(data);
    }).catch((error: unknown) => {
      if (controller.signal.aborted) return;
      setError(axios.isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message || 'Не вдалося розрахувати забезпечення замовлень.'
        : 'Не вдалося розрахувати забезпечення замовлень.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [days, selectedOrder, revision]);

  useEffect(() => {
    if (plan && selectedOrder) selectedRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [plan, selectedOrder]);

  const matches = (item: SupplyProduct) =>
    (showCovered || item.missing > 0) && item.productName.toLocaleLowerCase('uk-UA').includes(search.trim().toLocaleLowerCase('uk-UA'));
  const orders = (plan?.orders ?? []).map(order => ({ ...order, visible: order.products.filter(matches) }))
    .filter(order => order.visible.length > 0 || String(order.orderId) === selectedOrder
      || (showCovered && !search && order.products.length === 0));
  const products = (plan?.products ?? []).filter(matches);
  const shortageOrders = useMemo(() => plan?.orders.filter(order => order.products.some(item => item.missing > 0)).length ?? 0, [plan]);
  const selectOrder = (orderId: number) => {
    setMode('orders');
    setSearch('');
    setParams({ eventOrderId: String(orderId) });
  };

  return (
    <main className="page-shell order-supply-page">
      <ProcurementPlanningTabs />
      <h1>Забезпечення замовлень</h1>
      <p>Продукти для підтверджених подій. Запас розподіляється за датою події, для однакової дати — за номером замовлення.
        Наявні Н/Ф враховані; майбутні надходження не вважаються доступним запасом.</p>
      <div className="order-supply-controls">
        <label>Період
          <select value={days} onChange={event => { setDays(Number(event.target.value)); setParams({}); }}>
            <option value={3}>3 дні</option><option value={7}>7 днів</option><option value={30}>30 днів</option>
          </select>
        </label>
        <label>Пошук продукту<input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Назва продукту" /></label>
        <label><input type="checkbox" checked={showCovered} onChange={event => setShowCovered(event.target.checked)} />Показати забезпечені</label>
        <button type="button" onClick={() => setRevision(value => value + 1)} disabled={loading}>Оновити</button>
      </div>
      <div className="order-supply-controls" role="group" aria-label="Режим забезпечення">
        <button type="button" aria-pressed={mode === 'orders'} onClick={() => setMode('orders')}>По замовленнях</button>
        <button type="button" aria-pressed={mode === 'summary'} onClick={() => setMode('summary')}>Зведений список закупівлі</button>
      </div>
      {error && <div role="alert">{error}{selectedOrder &&
        <button type="button" onClick={() => setParams({})}>Переглянути актуальний список</button>}</div>}
      {loading && <p role="status">Розраховуємо потребу та розподіляємо залишки…</p>}
      {plan && !loading && <>
        <ProcurementAiAssistant plan={plan} />
        <p className="order-supply-overview">
          {date(plan.from)} — {date(plan.to)} · Замовлень: {plan.orders.length} · З дефіцитом: {shortageOrders} ·
          {' '}Продуктів докупити: {plan.products.filter(item => item.missing > 0).length}
          <span>Оновлено: {new Date(plan.generatedAtUtc).toLocaleString('uk-UA')}. Прочитання нагадування не усуває дефіцит.</span>
        </p>
        {mode === 'orders' ? <section aria-label="Потреби по замовленнях">
          {orders.length === 0 && <p>{plan.orders.length === 0 ? 'Немає підтверджених замовлень у цьому періоді.' : 'Немає позицій за вибраними фільтрами.'}</p>}
          {orders.map(order => <details key={order.orderId} className="order-supply-block"
            open={String(order.orderId) === selectedOrder ? true : undefined}
            ref={String(order.orderId) === selectedOrder ? selectedRef : undefined}>
            <summary><strong>№{order.orderId} · {date(order.eventDate)} · {order.eventName || order.customerName || 'Подія'}</strong>
              <span>{order.products.filter(item => item.missing > 0).length} дефіцитних позицій</span></summary>
            <Link to={`/management/event-orders?eventOrderId=${order.orderId}`}>Відкрити замовлення №{order.orderId}</Link>
            {order.visible.length === 0 && <p>Дефіциту за вибраними фільтрами немає. Потреба може покриватися наявними Н/Ф.</p>}
            {order.visible.map(item => <article className="order-supply-row" key={item.productId}>
              <strong>{item.productName}</strong><Amounts item={item} />
            </article>)}
          </details>)}
        </section> : <section aria-label="Зведена потреба">
          {products.length === 0 && <p>Немає продуктів для закупівлі за вибраними фільтрами.</p>}
          {products.map(item => <details className="order-supply-block" key={item.productId}>
            <summary><strong>{item.productName}</strong><span>Докупити: {quantity(item.missing)} {item.unit}
              {item.neededBy && ` · до ${date(item.neededBy)}`}</span></summary>
            <Amounts item={item} />
            {item.orders.map(order => <article className="order-supply-row" key={order.orderId}>
              <button type="button" onClick={() => selectOrder(order.orderId)}>№{order.orderId} · {date(order.eventDate)}</button>
              <Amounts item={{ ...order, unit: item.unit }} />
            </article>)}
          </details>)}
        </section>}
      </>}
    </main>
  );
};

export default OrderSupplyPage;
